import { useCallback, useState } from 'react';

import { workspaceHttp } from '@/lib/api/http-client';
import { notificationService } from '@/lib/services/notification.service';
import { i18n } from '@/shared/i18n';
import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';

export async function downloadBriefPdf(
  projectId: string,
  briefId: string,
  briefTitle: string,
): Promise<void> {
  const baseUrl = workspaceHttp.getConfig().baseURL ?? '';
  const response = await workspaceHttp.instance.get<ArrayBuffer>(
    `${baseUrl}/api/v1/projects/${projectId}/briefs/${briefId}/export`,
    { responseType: 'arraybuffer', withCredentials: true },
  );

  // The bytes are fetched here (authenticated request), so we hand the built
  // Blob to the shared sink rather than letting it fetch a URL itself.
  const blob = new Blob([response.data], { type: 'application/pdf' });
  // eslint-disable-next-line i18next/no-literal-string -- download filename slug, sanitized for the file system
  const fileName = `${sanitizeDownloadFileName(briefTitle, 'brief')}.pdf`;
  await downloadFile({ blob, fileName });
}

interface UseExportBriefPdfParams {
  projectId: string;
  briefId: string;
  briefTitle: string;
}

export function useExportBriefPdf({ projectId, briefId, briefTitle }: UseExportBriefPdfParams) {
  const [isExporting, setIsExporting] = useState(false);

  const exportPdf = useCallback(async () => {
    setIsExporting(true);
    try {
      await downloadBriefPdf(projectId, briefId, briefTitle);
    } catch {
      notificationService.showError(i18n.t('common:brief.useExportBriefPdf.failedToExportBriefAs'));
    } finally {
      setIsExporting(false);
    }
  }, [projectId, briefId, briefTitle]);

  return { exportPdf, isExporting };
}
